import type { ReactNode } from 'react';

import { cn } from '@/shared/lib';

import type { LoadingSkeletonProps, SkeletonShape } from './types';
import { loadingSkeletonStyles } from './styles';

const DEFAULT_SHAPE: SkeletonShape = 'list';
const DEFAULT_ROWS = 3;
const DEFAULT_LABEL = 'Loading';

/** How each shape draws one row. */
type RowVariant = 'line' | 'pair' | 'block';

const SHAPE_VARIANT: Record<SkeletonShape, RowVariant> = {
  text: 'line',
  list: 'pair',
  card: 'block',
  detail: 'pair',
};

interface SkeletonRowProps {
  variant: RowVariant;
  index: number;
}

function SkeletonRow({ variant, index }: SkeletonRowProps): ReactNode {
  return (
    <div
      key={index}
      aria-hidden="true"
      className={cn(
        loadingSkeletonStyles.row,
        variant === 'block' && loadingSkeletonStyles.rowBlock,
      )}
    >
      <div
        className={cn(
          loadingSkeletonStyles.bar,
          variant === 'line' && loadingSkeletonStyles.barShort,
        )}
      />
      {variant === 'pair' ? <div className={loadingSkeletonStyles.bar} /> : null}
    </div>
  );
}

/**
 * Placeholder geometry for content that has not arrived.
 *
 * `role="status"` plus `aria-live="polite"` makes this a live region that names
 * what is loading, and `aria-busy="true"` tells assistive tech the content it
 * sits where is still incomplete — so when the real content replaces it, that
 * is announced as an update rather than silence.
 *
 * Every placeholder bar is `aria-hidden="true"`. Without it a screen reader
 * walks a dozen anonymous rectangles before reaching anything real, and the
 * operator hears structure that carries no information.
 *
 * It draws real text-free nodes rather than a shimmer so that the page keeps its
 * height and does not jump when the content arrives.
 */
export function LoadingSkeleton({
  shape = DEFAULT_SHAPE,
  rows = DEFAULT_ROWS,
  label = DEFAULT_LABEL,
}: LoadingSkeletonProps): ReactNode {
  const variant = SHAPE_VARIANT[shape];
  const rowCount = Math.max(1, rows);

  return (
    <div
      role="status"
      aria-busy="true"
      aria-live="polite"
      aria-label={label}
      className={loadingSkeletonStyles.root}
    >
      {shape === 'detail' ? (
        <div aria-hidden="true" className={loadingSkeletonStyles.barTitle} />
      ) : null}

      {Array.from({ length: rowCount }, (_unused, index) => (
        <SkeletonRow key={index} variant={variant} index={index} />
      ))}
    </div>
  );
}
